import store from '../store';

import { logging } from "@pulte/pulte-myloan-npm";

// Things we want in the main chunk
// import Home from "views/Home";
// import Login from "views/Login";
// import Redirect from "views/Redirect";
// import InlineRegistrationQuestionnaire from "views/InlineRegistrationQuestionnaire";

import { createRouter, createWebHistory } from 'vue-router';
import { inject } from 'vue';

import Home from '@/views/Home.vue';
import Login from '@/views/Login.vue';
import Redirect from '@/views/Redirect.vue';
import InlineRegistrationQuestionnaire from '@/views/InlineRegistrationQuestionnaire.vue';

const scrollBehavior = (to, from, savedPosition) => {
    const isPageRefresh = !from || !from.name;
    if (savedPosition && !isPageRefresh) {
        // Return savedPosition only for popstate navigations
        return savedPosition;
    } else {
        const position = {};
        if (to.hash) {
            try {
                // scrollIntoView with the scrollIntoViewOptions does not support an offset so lets get a little crazy and trick the scroll into thinking
                // were above where we are so we can get that offset to eye reading level
                const element = document.getElementById(to.hash.substring(1));
                const pos = element.style.position;
                const top = element.style.top;
                element.style.position = "relative";
                element.style.top = "-290px";
                element.scrollIntoView({ behavior: "smooth" });
                element.style.position = pos;
                element.style.top = top;
            } catch (ex) {
                console.log('app: scroll: error: error occurred', ex);
                position.selector = to.hash;
                position.offset = { y: 290 };

                if (document.querySelector(to.hash)) {
                    return position;
                }
            }

            return false;
        }
        
        // Default scroll to top for new routes
        return { left: 0, top: 0 };
    }
};

const protect2FA = (to, from, next) => {
    let emailSet = store.state.twoFAEmail;
    
    if(emailSet){
        next()
     }
    else {
        next(`login`);
    }
}

const protectRoute = (to, from, next) => {
    console.log(`router: protecting route from ${from.fullPath} to ${to.fullPath}`);
    logging.logger.logPageView(to.fullPath, from.fullPath);

    let isAuthenticated = store.getters.isAuthenticated;
    if (isAuthenticated) {
        console.log(`router: user is authenticated, navigating to route ${to.fullPath}`);
        next();
    }
    else {
        var isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
        if (isIE11 === true) {
            console.log(`router: user is NOT authenticated, attempting to call loginRedirect`);
            // for IE11, redirect the user to the SSO to login or refresh their token
            return store.dispatch('loginRedirect', `${inject('config').loginBaseUrl}${encodeURI(to.fullPath)}`)
                .catch(err => {
                    console.log(`router: loginRedirect FAILED, manually navigating to login`, err);
                    return store.dispatch('redirectToLogin', inject('config'));
                });
        } else {

            console.log(`router: user is NOT authenticated, attempting to call loginSilent`);
            // attempt to log in silently
            return store.dispatch('loginSilent')
                .then(result => {
                    console.log(`router: loginSilent succeeded (${result}). navigating to ${to.fullPath}`);
                    next();
                }).catch(err => {
                    // if login silently failed (presumably because login is required)
                    // redirect to the login page utilizing the to.fullPath as our return URL
                    console.log(`router: loginSilent FAILED, navigating to login`, err);
                    next(`login?ReturnUrl=${encodeURI(to.fullPath)}`);
                });
        }
    }
};

var router = createRouter({
    history: createWebHistory(), 
    scrollBehavior,
    routes: [
        {
            path: "/",
            name: "home",
            component: Home,
            meta: {
                title: 'Finance your new home today.'
            }
        },
        {
            path: "/login",
            name: "login",
            component: Login,
            props: route => ({ redirectUri: route.query.ReturnUrl }),
            meta: {
                title: 'Securely log in to our website.'
            }
        },
        {
            path: "/twoStepVerification",
            name: "twoStepVerification",
            props: route => ({ redirectUri: route.query.ReturnUrl }),
            beforeEnter: protect2FA,
            component: () =>
            import(/* webpackChunkName: "twoStepVerification" */ "@/views/TwoStepVerification"),
            meta: {
                title: 'Two-Step Verification.'
            }
        },
        {
            path: "/loginhelp",
            name: "loginhelp",
            component: () =>
                import(/* webpackChunkName: "loginHelp" */ "@/views/LoginHelp"),
            meta: {
                title: 'Need help logging in?'
            }
        },
        {
            path: "/resetpassword",
            name: "resetpassword",
            component: () =>
                import(/* webpackChunkName: "resetPassword" */ "@/views/PasswordReset"),
            meta: {
                title: 'Reset your password.'
            }
        },
        {
            path: "/resetpasswordchange",
            name: "resetpasswordchange",
            component: () =>
                import(/* webpackChunkName: "[request]" */ "@/views/ResetPasswordChange"),
            meta: {
                title: 'Reset your password'
            }
        },
        {
            path: "/invalidpasswordlink",
            name: "invalidpasswordlink",
            component: () =>
                import(/* webpackChunkName: "[request]" */ "@/views/InvalidPasswordLink"),
            meta: {
                title: 'Invalid Password Link'
            }
        },
        {
            path: "/resources",
            name: "resources",
            component: () =>
                import(/* webpackChunkName: "learningResources" */ "@/views/LearningResources/LearningResources"),
            meta: {
                title: 'Use these resources to answer questions about our loans.'
            }
        },
        {
            path: "/registernow",
            name: "registernow",
            component: () => import(/* webpackChunkName: "registernow" */ "@/views/RegisterNow.vue"),
            meta: {
                title: 'Register to start the loan process.'
            }
        },
        {
            path: "/papertoweb",
            name: "papertoweb",
            component: () => import(/* webpackChunkName: "paperToWeb" */ "@/views/PaperToWeb.vue"),
            props: route => ({
                preRegisteredCustomerId: route.query.preRegisteredCustomerId,
                preRegisteredEmail: route.query.preRegisteredEmail
            }),
            meta: {
                title: 'Register your paper submission for MyLoan'
            }
        },
        {
            path: "/tho/:brand",
            name: "tho",
            component: () => import(/* webpackChunkName: "paperToWeb" */ "@/views/Tho.vue"),
            props: true,
            meta: {
                title: 'Transaction Home Online'
            }
        },
        {
            path: "/register/questionnaire",
            name: "registerquestionnaire",
            component: InlineRegistrationQuestionnaire,
            meta: {
                title: 'Getting started'
            }
        },
        {
            path: "/about",
            name: "about",
            component: () => import(/* webpackChunkName: "about" */ "@/views/About.vue"),
            meta: {
                title: 'Why choose Pulte Mortgage?'
            }
        },
        {
            path: "/account",
            beforeEnter: protectRoute,
            component: () => import(/* webpackChunkName: "account" */ "@/views/Account.vue"),
            children: [
                {
                    path: "username/change",
                    name: "usernamechange",
                    component: () =>
                        import(/* webpackChunkName: "account" */ "@/components/forms/UsernameChange.vue"),
                    props: route => ({ success: route.query.success }),
                    beforeEnter: protectRoute,
                    meta: {
                        title: 'Change your username.'
                    }
                },
                {
                    path: "password/change",
                    name: "passwordchange",
                    component: () =>
                        import(/* webpackChunkName: "account" */ "@/components/forms/PasswordChange.vue"),
                    beforeEnter: protectRoute,
                    meta: {
                        title: 'Change your password.'
                    }
                },
                {
                    path: "dateOfBirth/change",
                    name: "dateofbirthchange",
                    component: () =>
                        import(/* webpackChunkName: "account" */ "@/components/forms/DateOfBirthChange.vue"),
                    props: route => ({ success: route.query.success }),
                    beforeEnter: protectRoute,
                    meta: {
                        title: 'Change your date of birth.'
                    }
                }
            ]
        },
        {
            path: "/redirect",
            name: "redirect",
            component: Redirect,
            props: route => ({
                redirectUri: route.query.redirectUri,
                query: route.query
            }),
            beforeEnter: protectRoute,
            meta: {
                title: 'Redirecting you to your destination'
            }
        },
        {
            path: "/activate",
            name: "activate",
            component: () =>
                import(/* webpackChunkName: "activate" */ "@/views/Activate.vue"),
            meta: {
                title: 'Activate your account.'
            }
        },
        {
            path: "/activated",
            name: "registrationcomplete",
            component: () =>
                import(/* webpackChunkName: "registrationcomplete" */ "@/views/Activated.vue"),
            meta: {
                title: 'Your account has been successfully activated.'
            }
        },
        {
            path: "/activatednocta",
            name: "paregistrationcomplete",
            component: () =>
                import(/* webpackChunkName: "registrationcomplete" */ "@/views/ActivatedNoCTA.vue"),
            meta: {
                title: 'Your account has been successfully activated.'
            }
        },
        {
            path: "/lockedout",
            name: "lockedout",
            component: () =>
                import(/* webpackChunkName: "lockedOut" */ "@/views/Lockedout.vue"),
            meta: {
                title: 'Your account has been locked.'
            }
        },
        {
            path: "/privacy",
            name: "privacy",
            redirect: { name: "privacy-policy" },
            meta: {
                title: 'Your information is private with us.'
            }
        },
        {
            path: "/privacy-policy",
            name: "privacy-policy",
            component: () =>
                import(/* webpackChunkName: "privacy-policy" */ "@/views/PrivacyPolicy"),
            meta: {
                title: 'Your information is private with us.'
            }
        },
        {
          path: "/do-not-sell-or-share-my-personal-information",
          name: "do-not-sell-or-share-my-personal-information",
          component: () =>
            import(/* webpackChunkName: "privacy-policy" */ "@/views/DoNotSell"),
          meta: {
            title: "Do not sell or share my personal information",
          },
        },
        {
            path: "/termsofuse",
            name: "termsofuse",
            component: () =>
                import(/* webpackChunkName: "termsOfUse" */ "@/views/TermsOfUse"),
            meta: {
                title: 'Terms of use for our website.'
            }
        },
        {
            path: "/faq",
            name: "faq",
            component: () => import(/* webpackChunkName: "faq" */ "@/views/FAQ.vue"),
            meta: {
                title: 'Frequently asked technical questions.'
            }
        },
        {
            path: "/servicefees",
            name: "servicefees",
            component: () =>
                import(/* webpackchunkname: "servicefees" */ "@/views/ServiceFees.vue"),
            meta: {
                title: 'Common servicing fees and complimentary services.'
            }
        },
        {
            path: "/disaster-information",
            name: "disaster-information",
            component: () =>
                import(/* webpackchunkname: "disaster-information" */ "@/views/DisasterInformation"),
            meta: {
                title: 'Learn about disaster assistance.'
            }
        },
        {
            path: "/homeowner-counseling",
            name: "homeowner-counseling",
            component: () =>
                import(/* webpackchunkname: "homeowner-counseling" */ "@/views/HomeownerCounselingResources"),
            meta: {
                title: 'Providing financial advice and other homeowner resources.'
            }
        },
        {
            path: "/contact",
            name: "contactus",
            component: () =>
                import(/* webpackChunkName: "contactus" */ "@/views/ContactUs.vue"),
            meta: {
                title: 'Contact us with questions or feedback.'
            }
        },
        {
            path: "/afterclosing",
            name: "afterclosing",
            component: () =>
                import(/* webpackChunkName: "afterclosing" */ "../views/AfterClosing.vue"),
            meta: {
                title: 'What to know after closing.'
            }
        },
        {
            path: "/glossary",
            name: "glossary",
            component: () =>
                import(/* webpackChunkName: "glossary" */ "@/views/Glossary"),
            meta: {
                title: 'A glossary of mortgage terms.'
            }
        },
        {
            path: "/security",
            name: "security",
            component: () =>
                import(/* webpackChunkName: "security" */ "@/views/Security"),
            meta: {
                title: 'We take your privacy and security seriously.'
            }
        },
        {
            path: "/learn-more-about-making-payments",
            name: "learnmakepayment",
            component: () =>
                import(/* webpackChunkName: "learnpayment" */ "@/views/LearnMakePayment"),
            meta: {
                title: 'Making your mortgage payment.'
            }
        },
        {
            path: "/buying-power-calculator-faq",
            component: () =>
                import(/* webpackChunkName: "buyingPower" */ "@/views/BuyingPowerCalculatorFAQ"),
            children: [
                {
                    path: "credit-history",
                    name: "credithistory",
                    component: () =>
                        import(/* webpackChunkName: "buyingPower" */ "@/components/sections/buyingpowercalcfaq/CreditHistory"),
                    meta: {
                        title: 'Frequently asked questions about our calculator.'
                    }
                },
                {
                    path: "debt-and-income",
                    name: "debtandincome",
                    component: () =>
                        import(/* webpackChunkName: "buyingPower" */ "@/components/sections/buyingpowercalcfaq/DebtAndIncome"),
                    meta: {
                        title: 'Frequently asked questions about our calculator.'
                    }
                },
                {
                    path: "down-payment",
                    name: "downpayment",
                    component: () =>
                        import(/* webpackChunkName: "buyingPower" */ "@/components/sections/buyingpowercalcfaq/DownPayment"),
                    meta: {
                        title: 'Frequently asked questions about our calculator.'
                    }
                },
                {
                    path: "employment",
                    name: "employment",
                    component: () =>
                        import(/* webpackChunkName: "buyingPower" */ "@/components/sections/buyingpowercalcfaq/Employment"),
                    meta: {
                        title: 'Frequently asked questions about our calculator.'
                    }
                },
                {
                    path: "",
                    redirect: { name: "credithistory" }
                }
            ]
        },
        {
            path: "/mortgage-payment-help",
            name: "paymenthelp",
            component: () =>
                import(/* webpackChunkName: "paymenthelp" */ "@/views/PaymentHelp"),
            meta: {
                title: 'Experiencing financial difficulties or are you active duty military?'
            }
        },
        {
            path: "/top-mortgage-questions",
            name: "topquestions",
            component: () => import(/* webpackChunkName: "topquestions" */ "@/views/TopQuestions.vue"),
            children: [
                {
                    path: "before-closing",
                    name: "beforeclosing",
                    component: () =>
                        import(/* webpackChunkName: "topquestions" */ "@/components/sections/topquestions/BeforeClosing.vue"),
                    meta: {
                        title: 'Answering some of the most frequent questions before closing.'
                    }
                },
                {
                    path: "after-closing",
                    name: "after-closing",
                    component: () =>
                        import(/* webpackChunkName: "topquestions" */ "@/components/sections/topquestions/AfterClosing.vue"),
                    meta: {
                        title: 'Answering some of the most frequent questions after closing.'
                    }
                },
                {
                    path: "",
                    name: "DefaultQuestion", // Add a name to the default child route
                    redirect: { name: "beforeclosing" }
                }
            ]
        },
        {
            path: "/our-process",
            name: "Our-Process",
            component: () =>
                import(/* webpackChunkName: "paymenthelp" */ "@/views/OurProcess"),
            meta: {
                title: 'Our Loan Process is simple.'
            }
        },
        {
            path: "/equal-housing-lender",
            name: "Equal Housing Lender",
            component: () =>
                import(/* webpackChunkName: "paymenthelp" */ "@/views/EqualHousing"),
            meta: {
                title: 'Equal housing opportunity lender.'
            }
        },
        {
            path: "/browser-requirements",
            name: "Browser Requirements",
            component: () =>
                import(/* webpackChunkName: "paymenthelp" */ "@/views/BrowserRequirements"),
            meta: {
                title: 'Browser Requirements.'
            }
        },
        {
            path: "/importing-financial-documents",
            name: "Importing Financial Documents",
            component: () =>
                import(/* webpackChunkName: "paymenthelp" */ "@/views/ImportingFinancialDocuments"),
            meta: {
                title: 'Importing Financial Documents.'
            }
        },
        {
            path: "/mortgage-types",
            name: "Mortgage Types",
            component: () =>
                import(/* webpackChunkName: "mortgagetypes" */ "@/views/MortgageTypes"),
            meta: {
                title: 'Mortgage Types'
            }
        },
        {
            path: "/leadership",
            name: "leadership",
            component: () => import(/* webpackChunkName: "leadership" */ "@/views/Leadership.vue"),
            meta: {
                title: 'Meet our leadership team.'
            }
        },
        /* Special Announcements Pages */
        {
          beforeEnter: (to, from, next) => {
                let hasSpecialMessaging = inject('config').enableOutageMessaging;
              if (hasSpecialMessaging) next();
              else {
                  next('/');
              };
          },
          path: "/announcements/crisis-info",
          name: "Crisis Info",
          component: () => import(/* webpackChunkName: "covid-19" */ "../Views/Announcements/Covid-19"),
          meta: {
              title: 'Crisis Announcements'
          }
        },
        {
            path: "/announcements", /* Placeholder for now. Possibly add a separate Announcements page later on */
            redirect: "/"
        },
        {
            path: '/:catchAll(.*)', // Correct syntax for Vue Router 4
            name: "404",
            component: () =>
                import(/* webpackChunkName: "404" */ "@/views/PageNotFound.vue"),
            meta: {
                title: 'Page not found'
            }
        }
    ]
});

router.beforeEach((to, from, next) => {
    logging.logger.logPageView(to.fullPath, from.fullPath);
    document.title = to.meta.title;
    // ensure that the auth service is initialized
    if (store.getters.isAuthenticationInitialized === false) {
        console.log('router: authentication is not initialized');
              
        // Access global config from app.config.globalProperties
        let initializeConfig = { ...inject('config') }; 
        console.log(`initializing auth with config`, initializeConfig);
        store.dispatch('initializeAuth', initializeConfig).finally(() => {
            next();
        });
    } else {
        return next();
    }
});

export default router;
